import * as React from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import { api, getErrorMessage } from '@/api/api';
import { useToast } from '@/context/ToastContext';
import { Button } from '@/components/ui/Button';
import { AuthLayout } from '@/components/layout/AuthLayout';

type VerifyState = 'loading' | 'success' | 'error';

export function VerifyEmailPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { addToast } = useToast();
  const [state, setState] = React.useState<VerifyState>('loading');
  const [errorMessage, setErrorMessage] = React.useState('');
  const calledRef = React.useRef(false);

  const token = searchParams.get('token');

  React.useEffect(() => {
    if (calledRef.current) return;
    calledRef.current = true;

    if (!token) {
      setState('error');
      setErrorMessage('Missing verification token.');
      return;
    }

    api.auth
      .verifyEmail(token)
      .then(() => {
        setState('success');
        addToast({
          title: 'Email verified',
          subtitle: 'Your email has been verified. You can now sign in.',
          type: 'success',
        });
        setTimeout(() => navigate('/login', { replace: true }), 3000);
      })
      .catch((err) => {
        setState('error');
        setErrorMessage(getErrorMessage(err));
      });
  }, [token, navigate, addToast]);

  const handleBackToLogin = () => navigate('/login', { replace: true });

  return (
    <AuthLayout
      authType='verify-complete'
      onSwitch={handleBackToLogin}
      submitLabel=''
      dividerText=''
      formId='verify-email'
    >
      <div className='flex size-full flex-col items-center justify-center px-6 py-10'>
        {state === 'loading' && (
          <div className='flex flex-col items-center'>
            <svg
              className={clsx('mb-4 size-8 animate-spin', 'text-white md:text-brand-800')}
              xmlns='http://www.w3.org/2000/svg'
              fill='none'
              viewBox='0 0 24 24'
            >
              <circle
                className='opacity-25'
                cx='12'
                cy='12'
                r='10'
                stroke='currentColor'
                strokeWidth='4'
              />
              <path
                className='opacity-75'
                fill='currentColor'
                d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
              />
            </svg>
            <p className={clsx('font-sans text-18 font-medium', 'text-white md:text-brand-750')}>
              Verifying your email...
            </p>
          </div>
        )}

        {state === 'success' && (
          <div className='flex flex-col items-center text-center'>
            <svg
              className={clsx('mb-4 size-16', 'text-green-400 md:text-green-600')}
              xmlns='http://www.w3.org/2000/svg'
              fill='none'
              viewBox='0 0 24 24'
              strokeWidth={1.5}
              stroke='currentColor'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                d='M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z'
              />
            </svg>
            <h2
              className={clsx(
                'font-proxima font-bold leading-25 tracking-normal',
                'mb-3 text-[30px] text-white',
                'md:text-[38px] md:text-brand-800'
              )}
            >
              Email verified
            </h2>
            <p
              className={clsx(
                'font-sans font-normal leading-21 tracking-normal',
                'text-14 text-white',
                'md:text-[18px] md:text-brand-750'
              )}
            >
              Redirecting to login...
            </p>
          </div>
        )}

        {state === 'error' && (
          <div className='flex flex-col items-center text-center'>
            <svg
              className={clsx('mb-4 size-16', 'text-red-400 md:text-red-500')}
              xmlns='http://www.w3.org/2000/svg'
              fill='none'
              viewBox='0 0 24 24'
              strokeWidth={1.5}
              stroke='currentColor'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                d='M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z'
              />
            </svg>
            <h2
              className={clsx(
                'font-proxima font-bold leading-25 tracking-normal',
                'mb-3 text-[30px] text-white',
                'md:text-[38px] md:text-brand-800'
              )}
            >
              Verification failed
            </h2>
            <p
              className={clsx(
                'font-sans font-normal leading-21 tracking-normal',
                'mb-8 text-14 text-white',
                'md:text-[18px] md:text-brand-750'
              )}
            >
              {errorMessage}
            </p>
            <Button
              type='button'
              variant='purple-dark'
              className={clsx(
                'h-[54px] w-full max-w-[385px]',
                'rounded-30 text-16 font-bold uppercase tracking-wide'
              )}
              onClick={handleBackToLogin}
            >
              BACK TO LOGIN
            </Button>
          </div>
        )}
      </div>
    </AuthLayout>
  );
}

export default VerifyEmailPage;
